/**
 * The content of the teaching "Live activity" panel — pure data, no React and
 * no React Native, so it can be tested without an Expo runtime.
 *
 * Split out of TeachingLiveActivityStream.tsx for exactly that reason: the
 * component imports @expo/vector-icons, which vitest cannot resolve, and the
 * thing worth testing here is which section names get narrated.
 */
export type Cat =
    "read" | "who" | "match" | "write" | "level" | "llm" | "check" | "persist" | "done";

export interface Message {
    cat: Cat;
    // Use **word** for accent (rendered teal+bold).
    text: string;
}

/**
 * Fallback section names, used only when the caller supplies none.
 *
 * These are the STANDARD ASSESSMENT's sections. On a special visit none of them
 * is on the form, so the panel narrated "Reading Neurological answers" to a
 * nurse doing a fall follow-up while never naming Fall Event, Fall Injury or
 * Mobility & Transfers. Callers pass the visit's real sections instead; this
 * list survives only so the component still renders if one forgets.
 */
export const FALLBACK_SECTIONS = [
    "Vital Signs",
    "Pain",
    "Neurological",
    "EENT",
    "Cardiopulmonary",
    "Musculoskeletal",
    "GI/GU/Reproductive",
    "Integumentary",
    "Allergies and Nutrition",
    "DME and Supplies",
    "Safety, Emergency and Immunization",
    "Medications",
    "Visit Info"
];

export const TOPICS = [
    "Low-sodium diet",
    "Fall prevention at home",
    "Insulin storage and rotation",
    "Signs of infection",
    "Foley care and bag emptying",
    "Pressure-injury prevention",
    "Daily weight and swelling log",
    "Inhaler technique",
    "Reporting pain on a 0-10 scale",
    "Medication schedule and refills",
    "Safe transfer with a walker",
    "When to call 911",
    "Blood-sugar checks and logging",
    "Wound-dressing changes",
    "Constipation and fluid intake",
    "Oxygen safety at home",
    "Ostomy pouch changes",
    "Skin checks over bony areas"
];

const AUDIENCE = ["patient", "primary caregiver", "daughter (caregiver)", "patient and caregiver"];

/**
 * `answerCount` is threaded through because the line used to read a hardcoded
 * "236 assessment answers" on every visit. A light special visit carries a
 * couple of dozen.
 */
function plumbing(answerCount?: number): Message[] {
    return [
        {
            cat: "read",
            text:
                answerCount == null
                    ? "Loading **assessment answers** into context"
                    : `Loading **${answerCount} assessment answers** into context`
        },
        { cat: "read", text: "Compressing the **diagnosis** block" },
        { cat: "read", text: "Compressing the **medications** block" },
        { cat: "llm", text: "Building the **teaching-selector** system prompt" },
        { cat: "llm", text: "Calling **Claude Sonnet 4.6** for topic selection" },
        { cat: "llm", text: "Streaming response from **Claude**..." },
        { cat: "write", text: "Expanding each topic into **patient-facing language**" },
        { cat: "check", text: "Removing **clinical jargon** from drafts" },
        { cat: "check", text: "Checking teaching against **documented allergies**" },
        { cat: "check", text: "De-duplicating overlapping **teaching items**" },
        { cat: "level", text: "Scoring **readability** on every item" },
        { cat: "persist", text: "Persisting **teaching_items** rows" },
        { cat: "persist", text: "Writing ai_call_logs audit row for **teaching**" },
        { cat: "done", text: "**Teaching drafted** — ready for your review" }
    ];
}

export function buildMessages(
    sections: string[],
    topics: string[],
    answerCount?: number
): Message[] {
    const out: Message[] = [];
    for (const section of sections) {
        out.push({ cat: "read", text: `Reading **${section}** answers` });
        out.push({ cat: "match", text: `Looking for teachable findings in **${section}**` });
    }
    for (const topic of topics) {
        out.push({ cat: "match", text: `Matching teaching topic **${topic}**` });
        out.push({ cat: "write", text: `Drafting teaching for **${topic}**` });
        out.push({
            cat: "level",
            text: `Rewriting **${topic}** at a 6th-grade reading level`
        });
    }
    for (const who of AUDIENCE) {
        out.push({ cat: "who", text: `Deciding to educate the **${who}**` });
    }
    out.push(...plumbing(answerCount));
    return out;
}
